import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ParallaxHero from '../components/ParallaxHero';
import { 
  Shield, CheckCircle, FileText, Users, Scale, Clock, 
  ArrowRight, Award, BookOpen
} from 'lucide-react';

const GovernanceSuitePage = () => {
  const navigate = useNavigate();
  const [benefitsVisible, setBenefitsVisible] = useState(false);
  const [servicesVisible, setServicesVisible] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  
  const benefitsRef = useRef(null);
  const servicesRef = useRef(null);
  const ctaRef = useRef(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = { threshold: 0.15, rootMargin: '0px 0px -50px 0px' };
    
    const createObserver = (setVisible) => new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      });
    }, observerOptions);

    const benefitsObserver = createObserver(setBenefitsVisible);
    const servicesObserver = createObserver(setServicesVisible);
    const ctaObserver = createObserver(setCtaVisible);

    if (benefitsRef.current) benefitsObserver.observe(benefitsRef.current);
    if (servicesRef.current) servicesObserver.observe(servicesRef.current);
    if (ctaRef.current) ctaObserver.observe(ctaRef.current);

    return () => {
      benefitsObserver.disconnect();
      servicesObserver.disconnect();
      ctaObserver.disconnect();
    };
  }, []);

  // Warm Premium Palette - Consistent with Homepage
  const colors = {
    primaryTeal: '#3D6B67',
    primaryTealLight: '#4A7D78',
    primaryBrown: '#5C4033',
    primaryBrownDark: '#4A3328',
    bgCream: '#FAF6F2',
    bgSage: '#DCE7E3',
    bgCard: '#FFFFFF',
    bgWarmWhite: '#FFFDFB',
    textPrimary: '#2C1810',
    textSecondary: '#5D4E47',
    textTertiary: '#8B7D76',
    textOnDark: '#FFFFFF',
    textOnDarkMuted: '#E8E4E1',
    borderWarm: '#E8E0D8',
    borderSage: '#C5D5CF',
    success: '#4D9078',
  };

  const services = [
    {
      icon: Shield,
      title: "Due Diligence",
      description: "Comprehensive background verification and assessment of NGO partners to ensure credibility and capability.",
      features: ["Financial health check", "Legal compliance review", "Track record verification", "Capacity assessment"]
    },
    {
      icon: Scale,
      title: "Compliance Management",
      description: "End-to-end support for CSR regulatory compliance including Schedule VII, CSR-2, and BRSR requirements.",
      features: ["Schedule VII alignment", "CSR-2 form preparation", "BRSR reporting", "Auditor coordination"]
    },
    {
      icon: FileText,
      title: "Social Audit",
      description: "Independent verification of project outcomes, beneficiary impact, and fund utilization.",
      features: ["Field verification", "Beneficiary interviews", "Impact documentation", "Third-party validation"]
    },
    {
      icon: Users,
      title: "Strategic Advisory",
      description: "Expert guidance on CSR strategy, policy development, and program design aligned with business objectives.",
      features: ["CSR policy drafting", "Program design", "SDG mapping", "Government convergence"]
    }
  ];

  const benefits = [
    { icon: CheckCircle, title: "Risk Mitigation", desc: "Minimize reputational and compliance risks through thorough due diligence" },
    { icon: Clock, title: "Time Savings", desc: "Let experts handle compliance while you focus on impact" },
    { icon: Award, title: "Credibility", desc: "Build stakeholder trust with verified and audited outcomes" },
    { icon: BookOpen, title: "Expertise", desc: "Access domain specialists with deep CSR knowledge" }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bgCream }}>
      <Header />

      {/* Hero Section - Background image like homepage */}
      <section 
        className="relative min-h-[70vh] overflow-hidden"
        data-testid="governance-hero-section"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_ecoconsu/artifacts/xlv79rob_image.png)',
            backgroundPosition: 'center center',
          }}
        />
        
        {/* Gradient overlay for text readability */}
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to right, 
              rgba(44, 24, 16, 0.7) 0%, 
              rgba(61, 107, 103, 0.5) 40%, 
              rgba(61, 107, 103, 0.3) 65%,
              rgba(61, 107, 103, 0.15) 100%
            )`
          }}
        />
        
        {/* Content - Vertically centered */}
        <div className="relative z-10 min-h-[70vh] flex items-center">
          <div className="w-full py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
              
              {/* Hero Content */}
              <div className="max-w-xl lg:max-w-2xl">
                
                {/* Small badge */}
                <div 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 opacity-0 animate-fade-in-up"
                  style={{ 
                    animationDelay: '0.1s', 
                    animationFillMode: 'forwards',
                    backgroundColor: 'rgba(77, 144, 120, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.25)'
                  }}
                >
                  <Shield className="w-4 h-4" style={{ color: '#FFFFFF' }} />
                  <span 
                    className="text-xs font-semibold tracking-widest uppercase"
                    style={{ color: '#FFFFFF' }}
                  >
                    Compliance & Governance
                  </span>
                </div>
                
                {/* Main Heading */}
                <h1 
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05] tracking-tight opacity-0 animate-fade-in-up"
                  style={{ 
                    color: '#FFFFFF',
                    textShadow: '0 4px 30px rgba(0,0,0,0.5), 0 2px 10px rgba(0,0,0,0.3)',
                    animationDelay: '0.2s', 
                    animationFillMode: 'forwards'
                  }}
                  data-testid="governance-hero-title"
                >
                  Governance<br />Suite
                </h1>

                {/* Subheading */}
                <p 
                  className="text-lg md:text-xl mb-8 leading-relaxed max-w-lg opacity-0 animate-fade-in-up"
                  style={{ 
                    color: 'rgba(255, 255, 255, 0.95)',
                    textShadow: '0 2px 15px rgba(0,0,0,0.4)',
                    animationDelay: '0.3s', 
                    animationFillMode: 'forwards'
                  }}
                >
                  Enterprise-grade compliance, due diligence, and social audit services for credible, measurable CSR outcomes.
                </p>

                {/* CTA Button */}
                <div 
                  className="opacity-0 animate-fade-in-up"
                  style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
                >
                  <button 
                    onClick={() => navigate('/contact')}
                    className="inline-flex items-center gap-2.5 px-7 py-4 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5"
                    style={{ 
                      backgroundColor: '#4D8B86',
                      color: '#FFFFFF',
                      border: '2px solid rgba(255, 255, 255, 0.6)',
                      boxShadow: '0 4px 14px rgba(61, 107, 103, 0.4)'
                    }}
                    data-testid="governance-cta-btn"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#5A9A95';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.9)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#4D8B86';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                    }}
                  >
                    Get Started
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section ref={benefitsRef} className="py-10" style={{ backgroundColor: colors.bgSage, borderBottom: `1px solid ${colors.borderSage}` }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {benefits.map((benefit, idx) => (
              <div 
                key={idx} 
                className={`flex items-start gap-3 transition-all duration-500 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: benefitsVisible ? `${idx * 100}ms` : '0ms' }}
              >
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: colors.bgCard }}
                >
                  <benefit.icon className="w-5 h-5" style={{ color: colors.primaryTeal }} />
                </div>
                <div>
                  <p className="font-semibold text-sm" style={{ color: colors.textPrimary }}>{benefit.title}</p>
                  <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section ref={servicesRef} className="py-20 md:py-28 px-6" style={{ backgroundColor: colors.bgCream }}>
        <div className="max-w-6xl mx-auto">
          <div 
            className={`text-center mb-14 transition-all duration-700 ${servicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: colors.textTertiary }}>
              Our Services
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight" style={{ color: colors.textPrimary }}>
              Comprehensive Governance Solutions
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, idx) => (
              <div
                key={idx}
                className={`rounded-2xl p-8 transition-all duration-500 group ${servicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ 
                  backgroundColor: colors.bgCard, 
                  border: `1px solid ${colors.borderWarm}`,
                  transitionDelay: servicesVisible ? `${200 + idx * 150}ms` : '0ms'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = colors.primaryTeal;
                  e.currentTarget.style.boxShadow = `0 0 0 1px ${colors.primaryTeal}, 0 12px 24px rgba(61, 107, 103, 0.1)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = colors.borderWarm;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                  style={{ backgroundColor: colors.bgSage }}
                >
                  <service.icon className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" style={{ color: colors.primaryTeal }} />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: colors.textPrimary }}>{service.title}</h3>
                <p className="text-sm leading-relaxed mb-6" style={{ color: colors.textSecondary }}>{service.description}</p>
                <div className="space-y-2">
                  {service.features.map((feature, fidx) => (
                    <div key={fidx} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" style={{ color: colors.success }} />
                      <span className="text-sm" style={{ color: colors.textPrimary }}>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="py-20 md:py-28 px-6" style={{ backgroundColor: colors.primaryTeal }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className={`text-2xl md:text-3xl lg:text-4xl font-bold mb-6 tracking-tight transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ color: colors.textOnDark }}
          >
            Ready to Strengthen Your Governance?
          </h2>
          <p 
            className={`text-lg mb-10 max-w-2xl mx-auto transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ color: colors.textOnDarkMuted, transitionDelay: ctaVisible ? '150ms' : '0ms' }}
          >
            Schedule a consultation with our experts to discuss your compliance needs and governance requirements.
          </p>
          <div 
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: ctaVisible ? '300ms' : '0ms' }}
          >
            <button 
              onClick={() => navigate('/contact')}
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5"
              style={{ 
                backgroundColor: colors.primaryTeal,
                color: colors.textOnDark,
                boxShadow: '0 4px 14px rgba(61, 107, 103, 0.4)'
              }}
              data-testid="governance-consultation-btn"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.primaryTealLight}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primaryTeal}
            >
              Schedule Consultation
              <ArrowRight className="w-5 h-5" />
            </button>
            <button 
              onClick={() => navigate('/csr-landscape')}
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl font-semibold transition-all duration-200"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                color: colors.textOnDark,
                border: '1.5px solid rgba(255, 255, 255, 0.3)'
              }}
              data-testid="governance-landscape-btn"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              }}
            >
              Learn CSR Landscape
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GovernanceSuitePage;
